import { FC, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Stack } from '@mui/material'
import { DateTime } from 'luxon'
import type { EngagementClip } from './types'
import { InspectPlayerForCamera, type InspectPlayerCameraHandle } from './InspectPlayerForCamera'
import { InspectModePlayerControls } from './Controls/InspectModePlayerControls'
import { PlayerPlaceholder } from './PlayerPlaceholder'
import { isClipPlayable } from './utils/ertInspectUtils'
import { Banner, BannerType } from '../../../../components/Banner/Banner'
import { Camera, CameraRecording } from '../../../../api/models'
import { getAspectRatioFromCamera } from '../../../../utils/utils'
import { getDisallowedReasonMessage } from './utils/messagesUtils'

export type EngagementRecordingVideoElement = {
  recording: EngagementClip
  videoEl: HTMLVideoElement
  // True while the source is switching to another clip (seek / auto-advance) - until the new source is
  // ready, currentTime still belongs to the old clip, so the timeline scrubber freezes.
  isSourceSwapping: boolean
}

export type InspectPlayerHandle = {
  // Keyed by cameraId (one player per camera). Each value's `recording` is that camera's current clip.
  videoElements: Map<string, EngagementRecordingVideoElement>
  seekCamera: (cameraId: string, absoluteTimeMs: number) => void
}

type InspectPlayerProps = {
  ref?: Ref<InspectPlayerHandle>
  cameras: Camera[]
  recordingsByCameraId: Map<string, EngagementClip[]> // already grouped by camera; from getEngagementRecordings
  selectedCameraId: string | null
  isSidePanelOpen: boolean
  onToggleSidePanel: () => void
  onVideoElementsChange: (videoElements: Map<string, EngagementRecordingVideoElement>) => void
  // When the engagement ended (last activity completed); null while activities load.
  engagementEndEpoch: number | null
  // Denied cameras, keyed by cameraId, with the denial reason. A denied camera has no clips, so no player
  // (and no per-clip error banner) ever mounts for it - selecting it shows this reason instead.
  deniedReasonByCameraId: Map<string, CameraRecording['disallowedReason']>
}

const clipStartMs = (clip: EngagementClip): number => DateTime.fromISO(clip.startTime).toMillis()
const clipEndMs = (clip: EngagementClip): number => DateTime.fromISO(clip.endTime).toMillis()

const bannerSx = { marginY: 'var(--spacing-l)' }

// One VideoPlayer per camera: when a clip ends (or a seek crosses clips), that camera's player swaps the
// source on its one video element
export const InspectPlayer: FC<InspectPlayerProps> = ({
  ref,
  cameras,
  recordingsByCameraId,
  selectedCameraId,
  isSidePanelOpen,
  onToggleSidePanel,
  onVideoElementsChange,
  engagementEndEpoch,
  deniedReasonByCameraId
}) => {
  const [videoElements, setVideoElements] = useState<Map<string, EngagementRecordingVideoElement>>(() => new Map())

  // Full camera handles for imperative routing (seekCamera) - a ref, not state, since a seek
  // shouldn't re-render.
  const cameraHandlesByCameraId = useRef<Map<string, InspectPlayerCameraHandle>>(new Map())

  // One cached ref-callback per camera. An inline arrow would get a new identity every render, and React
  // detaches/reattaches a ref whose identity changed - each reattach calling setVideoElements, cascading
  // into a render loop.
  const refCallbacksByCameraId = useRef<Map<string, (handle: InspectPlayerCameraHandle | null) => void>>(new Map())

  // Keep only what can actually play: drop clips starting at/after the engagement end (straddlers stay -
  // InspectPlayerForCamera stops them at the boundary), sort each camera's clips by start time into a
  // copy (the source arrays are shared with InspectTimelines), and drop cameras left with no clips
  // (InspectPlayerForCamera reads clips[0]).
  const clipsByCameraId = useMemo(() => {
    const map = new Map<string, EngagementClip[]>()
    for (const [cameraId, clips] of recordingsByCameraId) {
      const playableClips = engagementEndEpoch === null ? [...clips] : clips.filter(clip => isClipPlayable(clip, engagementEndEpoch))
      if (playableClips.length > 0) {
        map.set(
          cameraId,
          playableClips.sort((a, b) => clipStartMs(a) - clipStartMs(b))
        )
      }
    }
    return map
  }, [recordingsByCameraId, engagementEndEpoch])

  const selectedEntry = selectedCameraId ? videoElements.get(selectedCameraId) : undefined

  const getRefCallbackForCamera = useCallback((cameraId: string) => {
    const cached = refCallbacksByCameraId.current.get(cameraId)
    if (cached) {
      return cached
    }
    const callback = (handle: InspectPlayerCameraHandle | null) => {
      if (handle) {
        cameraHandlesByCameraId.current.set(cameraId, handle)
      } else {
        cameraHandlesByCameraId.current.delete(cameraId)
      }
      setVideoElements(prev => {
        const next = new Map(prev)
        if (handle) {
          next.set(cameraId, { recording: handle.currentClip, videoEl: handle.videoEl, isSourceSwapping: handle.isSourceSwapping })
        } else {
          next.delete(cameraId)
        }
        return next
      })
    }
    refCallbacksByCameraId.current.set(cameraId, callback)
    return callback
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      videoElements,
      seekCamera: (cameraId: string, absoluteTimeMs: number) => {
        cameraHandlesByCameraId.current.get(cameraId)?.seek(absoluteTimeMs)
      }
    }),
    [videoElements]
  )

  useEffect(() => {
    onVideoElementsChange?.(videoElements)
  }, [videoElements, onVideoElementsChange])

  return (
    <Stack alignItems="center" gap="var(--spacing-m)" width="100%" flex="1" minHeight="0">
      {Array.from(clipsByCameraId.entries()).map(([cameraId, clips]) => (
        <InspectPlayerForCamera
          key={cameraId}
          ref={getRefCallbackForCamera(cameraId)}
          aspectRatio={getAspectRatioFromCamera(cameras.find(c => c.uuid === cameraId)) ?? '16/9'}
          clips={clips}
          isSelected={cameraId === selectedCameraId}
          engagementEndEpoch={engagementEndEpoch}
        />
      ))}
      {selectedCameraId &&
        !clipsByCameraId.has(selectedCameraId) &&
        (deniedReasonByCameraId.has(selectedCameraId) ? (
          <Banner
            type={BannerType.Error}
            title={getDisallowedReasonMessage(deniedReasonByCameraId.get(selectedCameraId))}
            sx={bannerSx}
            data-testid="video-access-error-banner"
          />
        ) : (
          <PlayerPlaceholder />
        ))}
      {selectedEntry && (
        <InspectModePlayerControls
          key={selectedEntry.recording.recordingId}
          video={selectedEntry.videoEl}
          startTimeInMs={clipStartMs(selectedEntry.recording)}
          fallbackDurationInSeconds={(clipEndMs(selectedEntry.recording) - clipStartMs(selectedEntry.recording)) / 1000}
          isSidePanelOpen={isSidePanelOpen}
          onToggleSidePanel={onToggleSidePanel}
        />
      )}
    </Stack>
  )
}

InspectPlayer.displayName = 'InspectPlayer'
